/**
 * Hook to resolve conversation participants to ChatParticipant format.
 * Uses organization membership data to get user details from Clerk.
 */

import type { ConversationParticipants } from '@saaga/shared/interfaces/conversation.interface'
import { useMemo } from 'react'

import type { ChatParticipant } from '@/components/chat/chat-message.interface'
import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'

interface UseChatParticipantsParams {
  participants: ConversationParticipants | undefined
  userLookup: UserLookupMap
}

export const useChatParticipants = ({
  participants,
  userLookup,
}: UseChatParticipantsParams): ChatParticipant[] =>
  useMemo(() => {
    if (!participants) return []

    const participantList: ChatParticipant[] = []

    for (const userId of Object.keys(participants)) {
      const lookupEntry = userLookup.get(userId)

      if (lookupEntry) {
        participantList.push({
          id: lookupEntry.id,
          name: lookupEntry.name,
          avatarUrl: lookupEntry.avatarUrl,
        })
        continue
      }

      participantList.push({
        id: userId,
        name: 'Unknown User',
        avatarUrl: undefined,
      })
    }

    return participantList
  }, [participants, userLookup])
